import { useEffect, useState, useSyncExternalStore } from 'react';
import { ApiError, api } from './api';

/**
 * Whether routes are drawn along the roads, and the shapes to draw them with.
 *
 * Two maps ask the same question -- the board and the one in a route's drawer --
 * and they have to agree. Two `useState`s reading the same localStorage key
 * would not: turning roads on in the drawer, closing it and finding the board
 * still drawing straight lines reads as broken rather than as two preferences.
 * So the preference is one value outside React, and both maps subscribe.
 *
 * Straight lines stay the default. They are what we know for certain from the
 * plan alone and they need no routing engine, which is also what makes them
 * right on an installation that has none.
 */

const KEY = 'allrouted.map.roads';

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    /* Private windows and blocked site data: the default is fine. */
    return false;
  }
}

let current = read();
const listeners = new Set<() => void>();

function set(next: boolean): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(KEY, next ? '1' : '0');
  } catch {
    /* Not being able to remember it is not a reason to fail. */
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The shared switch. Every map that reads it moves together. */
export function useRoadShapes(): [boolean, (next: boolean) => void] {
  const on = useSyncExternalStore(
    subscribe,
    () => current,
    /* No localStorage while rendering on a server; straight lines there. */
    () => false,
  );
  return [on, set];
}

export interface RoadPaths {
  /** False where there is no routing engine to ask. */
  available: boolean;
  reason?: string;
  /** Route id to its driven shape, `[lng, lat]` in GeoJSON order. */
  paths: Record<string, [number, number][]>;
}

/**
 * The road shapes for a day, fetched when the switch is on.
 *
 * Keyed on the plan's version rather than on the board object, which the live
 * stream patches many times a minute: an arrival does not change where the
 * roads are, and refetching on every one would be a request per ping. The
 * server caches by coordinate sequence, so two maps asking at once is one
 * question for the routing engine.
 */
export function useRoadPaths(
  territoryId: string | null,
  date: string,
  enabled: boolean,
  planVersion: number | null,
): { roads: RoadPaths | null; loading: boolean } {
  const [roads, setRoads] = useState<RoadPaths | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !territoryId || !date) {
      setRoads(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .routePaths(territoryId, date)
      .then((result) => {
        if (!cancelled) {
          setRoads({ available: result.available, reason: result.reason, paths: result.paths });
        }
      })
      .catch((err: unknown) => {
        /* Loud, and the straight lines stay: the map is still correct. */
        if (!cancelled) {
          setRoads({
            available: false,
            reason: err instanceof ApiError ? err.message : String(err),
            paths: {},
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, territoryId, date, planVersion]);

  return { roads, loading };
}
